/**
 * ─────────────────────────────────────────────────────────────────────
 * FILE: src/pages/Billing.tsx
 * DESCRIPTION:
 * Professional billing and subscription management page using Clerk Billing.
 *
 * MVP ARCHITECTURE:
 * - Clerk Dashboard is the source of truth for plans and pricing.
 * - Clerk PricingTable handles plan selection and checkout.
 * - Clerk UserProfile handles account and billing management.
 * - Plans are loaded dynamically from Clerk for contextual information.
 * - No hard-coded plan prices.
 * - No fake Free plan.
 * - No mock invoices or payment methods.
 * - Upload limits / feature entitlements will be enforced separately.
 *
 * CURRENT CLERK USER PLANS:
 * - introductory_offer
 * - business
 *
 * @copyright 2025-2026 Anou. All rights reserved.
 * @version 5.0.0
 * @updated 2026-08-29
 * ─────────────────────────────────────────────────────────────────────
 */

import { useEffect, useState } from 'react'
import {
  PricingTable,
  UserProfile,
  useClerk,
  useUser,
} from '@clerk/react'
import {
  AlertCircle,
  Check,
  ChevronRight,
  CreditCard,
  Settings,
  Shield,
  Sparkles,
  UserRound,
} from 'lucide-react'

// ─────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────

interface BillingPlan {
  id: string
  name: string
  slug: string
  description?: string | null

  fee?: {
    amount: number
    amountFormatted?: string
    currency?: string
    currencySymbol?: string
  } | null

  annualMonthlyFee?: {
    amount: number
    amountFormatted?: string
    currency?: string
    currencySymbol?: string
  } | null

  annualFee?: {
    amount: number
    amountFormatted?: string
    currency?: string
    currencySymbol?: string
  } | null

  freeTrialEnabled?: boolean
  freeTrialDays?: number | null

  publiclyVisible?: boolean
  isRecurring?: boolean
  hasBaseFee?: boolean

  features?: Array<{
    id: string
    name: string
    description?: string | null
  }>
}

type BillingTab = 'subscription' | 'account'

// ─────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────

const formatPlanPrice = (plan: BillingPlan): string | null => {
  if (!plan.fee) return null

  const symbol = plan.fee.currencySymbol || '$'
  const amount =
    plan.fee.amountFormatted ||
    (plan.fee.amount / 100).toFixed(2)

  return `${symbol}${amount}/month`
}

const formatAnnualPrice = (plan: BillingPlan): string | null => {
  if (!plan.annualFee) return null

  const symbol = plan.annualFee.currencySymbol || '$'
  const amount =
    plan.annualFee.amountFormatted ||
    (plan.annualFee.amount / 100).toFixed(2)

  return `${symbol}${amount}/year`
}

const getTrialText = (plan: BillingPlan): string | null => {
  if (!plan.freeTrialEnabled || !plan.freeTrialDays) {
    return null
  }

  return `${plan.freeTrialDays}-day trial`
}

// ─────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────

const Billing: React.FC = () => {
  const { isLoaded } = useUser()
  const clerk = useClerk()

  const [activeTab, setActiveTab] =
    useState<BillingTab>('subscription')

  const [isDarkMode, setIsDarkMode] = useState(false)

  const [plans, setPlans] = useState<BillingPlan[]>([])
  const [plansLoading, setPlansLoading] = useState(true)
  const [plansError, setPlansError] = useState<string | null>(null)

  // ───────────────────────────────────────────────────────────────────
  // Theme detection
  // ───────────────────────────────────────────────────────────────────

  useEffect(() => {
    const updateTheme = () => {
      setIsDarkMode(
        document.documentElement.classList.contains('dark'),
      )
    }

    updateTheme()

    const observer = new MutationObserver(updateTheme)

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    })

    return () => observer.disconnect()
  }, [])

  // ───────────────────────────────────────────────────────────────────
  // Load User Billing Plans from Clerk
  //
  // Clerk remains the source of truth.
  // We deliberately do not hard-code prices or trial durations here.
  // ───────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!isLoaded) return

    let cancelled = false

    const loadPlans = async () => {
      try {
        setPlansLoading(true)
        setPlansError(null)

        const response = await clerk.billing.getPlans({
          for: 'user',
        })

        if (cancelled) return

        setPlans((response?.data || []) as BillingPlan[])
      } catch (error) {
        console.error('Failed to load Clerk billing plans:', error)

        if (!cancelled) {
          setPlansError(
            'We could not load billing plan information right now.',
          )
        }
      } finally {
        if (!cancelled) {
          setPlansLoading(false)
        }
      }
    }

    loadPlans()

    return () => {
      cancelled = true
    }
  }, [clerk, isLoaded])

  // ───────────────────────────────────────────────────────────────────
  // Loading
  // ───────────────────────────────────────────────────────────────────

  if (!isLoaded) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 transition-colors duration-200 dark:bg-gray-950 sm:p-8">
        <div className="mx-auto flex min-h-[400px] max-w-7xl items-center justify-center">
          <div
            className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent"
            aria-label="Loading billing"
          />
        </div>
      </div>
    )
  }

  // ───────────────────────────────────────────────────────────────────
  // Clerk Appearance
  // ───────────────────────────────────────────────────────────────────

  const clerkAppearance = {
    variables: {
      colorPrimary: '#2563eb',
      colorPrimaryForeground: '#ffffff',

      colorText: isDarkMode ? '#f9fafb' : '#111827',
      colorTextSecondary: isDarkMode ? '#9ca3af' : '#4b5563',

      colorBackground: isDarkMode ? '#111827' : '#ffffff',

      colorInputBackground: isDarkMode ? '#1f2937' : '#ffffff',
      colorInputText: isDarkMode ? '#f9fafb' : '#111827',

      colorNeutral: isDarkMode ? '#374151' : '#e5e7eb',

      borderRadius: '0.75rem',
    },

    elements: {
      rootBox: {
        width: '100%',
      },

      card: {
        backgroundColor: isDarkMode ? '#111827' : '#ffffff',
        borderColor: isDarkMode ? '#374151' : '#e5e7eb',
        color: isDarkMode ? '#f9fafb' : '#111827',
      },

      pricingTableCard: {
        backgroundColor: isDarkMode ? '#111827' : '#ffffff',
        borderColor: isDarkMode ? '#374151' : '#e5e7eb',
        color: isDarkMode ? '#f9fafb' : '#111827',
      },

      pricingTableCardTitle: {
        color: isDarkMode ? '#ffffff' : '#111827',
      },

      pricingTableCardDescription: {
        color: isDarkMode ? '#d1d5db' : '#4b5563',
      },

      pricingTableCardFeature: {
        color: isDarkMode ? '#e5e7eb' : '#374151',
      },

      button: {
        fontWeight: '600',
      },
    },
  }

  // ───────────────────────────────────────────────────────────────────
  // Render
  // ───────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 transition-colors duration-200 dark:bg-gray-950 dark:text-white">
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 sm:py-8 lg:px-8">

        {/* ============================================================
            HEADER
        ============================================================ */}

        <div className="mb-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <div className="mb-2 flex items-center gap-2 text-sm font-medium text-blue-600 dark:text-blue-400">
                <CreditCard className="h-4 w-4" />
                Account Management
              </div>

              <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-3xl">
                Billing & Subscription
              </h1>

              <p className="mt-2 max-w-2xl text-sm leading-6 text-gray-600 dark:text-gray-400 sm:text-base">
                Choose your plan, start your subscription, and manage
                your billing information securely through Clerk.
              </p>
            </div>
          </div>
        </div>

        {/* ============================================================
            TABS
        ============================================================ */}

        <div className="mb-8 border-b border-gray-200 dark:border-gray-800">
          <nav
            className="-mb-px flex gap-6 overflow-x-auto"
            aria-label="Billing sections"
          >
            <button
              type="button"
              onClick={() => setActiveTab('subscription')}
              className={`flex shrink-0 items-center gap-2 border-b-2 px-1 pb-3 text-sm font-medium transition-colors ${
                activeTab === 'subscription'
                  ? 'border-blue-600 text-blue-600 dark:border-blue-500 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:border-gray-700 dark:hover:text-gray-200'
              }`}
              aria-current={
                activeTab === 'subscription' ? 'page' : undefined
              }
            >
              <Sparkles className="h-4 w-4" />
              Plans & Subscription
            </button>

            <button
              type="button"
              onClick={() => setActiveTab('account')}
              className={`flex shrink-0 items-center gap-2 border-b-2 px-1 pb-3 text-sm font-medium transition-colors ${
                activeTab === 'account'
                  ? 'border-blue-600 text-blue-600 dark:border-blue-500 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:border-gray-700 dark:hover:text-gray-200'
              }`}
              aria-current={
                activeTab === 'account' ? 'page' : undefined
              }
            >
              <Settings className="h-4 w-4" />
              Account & Billing
            </button>
          </nav>
        </div>

        {/* ============================================================
            ACCOUNT & BILLING
        ============================================================ */}

        {activeTab === 'account' && (
          <section>
            <div className="mb-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Account & Billing
              </h2>

              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                Manage your profile, payment methods, subscription,
                and billing settings securely.
              </p>
            </div>

            <div
              className={`overflow-hidden rounded-2xl border shadow-sm ${
                isDarkMode
                  ? 'border-gray-700 bg-gray-900'
                  : 'border-gray-200 bg-white'
              }`}
            >
              <UserProfile
                routing="hash"
                appearance={clerkAppearance}
              />
            </div>
          </section>
        )}

        {/* ============================================================
            SUBSCRIPTION / PLANS
        ============================================================ */}

        {activeTab === 'subscription' && (
          <>
            {/* ========================================================
                BILLING INTRO
            ======================================================== */}

            <section className="mb-8 rounded-xl border border-blue-200 bg-blue-50 p-5 dark:border-blue-900/50 dark:bg-blue-950/20 sm:p-6">
              <div className="flex items-start gap-4">
                <div className="shrink-0 rounded-lg bg-blue-100 p-3 dark:bg-blue-900/30">
                  <Sparkles className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                </div>

                <div className="min-w-0">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Choose the plan that's right for you
                  </h2>

                  <p className="mt-2 text-sm leading-6 text-gray-700 dark:text-gray-300">
                    Select a subscription below. Clerk securely
                    handles checkout, payment methods, subscription
                    activation, and billing.
                  </p>

                  <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm text-gray-700 dark:text-gray-300">
                    <span className="inline-flex items-center gap-2">
                      <Check className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                      Secure checkout
                    </span>

                    <span className="inline-flex items-center gap-2">
                      <Check className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                      Monthly or annual billing
                    </span>

                    <span className="inline-flex items-center gap-2">
                      <Check className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                      Manage your subscription anytime
                    </span>
                  </div>
                </div>
              </div>
            </section>

            {/* ========================================================
                CLERK PLAN INFORMATION
            ======================================================== */}

            {!plansLoading && !plansError && plans.length > 0 && (
              <section className="mb-8">
                <div className="mb-5">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                    Available Plans
                  </h2>

                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    Plans and pricing are managed securely through Clerk.
                  </p>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  {plans.map((plan) => {
                    const monthlyPrice = formatPlanPrice(plan)
                    const annualPrice = formatAnnualPrice(plan)
                    const trialText = getTrialText(plan)

                    return (
                      <div
                        key={plan.id}
                        className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="min-w-0">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                              {plan.name}
                            </h3>

                            {plan.description && (
                              <p className="mt-1 text-sm leading-5 text-gray-600 dark:text-gray-400">
                                {plan.description}
                              </p>
                            )}
                          </div>

                          <div className="shrink-0 rounded-lg bg-blue-50 p-2 dark:bg-blue-900/20">
                            <Sparkles className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                          </div>
                        </div>

                        <div className="mt-5 flex flex-wrap gap-2">
                          {monthlyPrice && (
                            <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-200">
                              {monthlyPrice}
                            </span>
                          )}

                          {annualPrice && (
                            <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-200">
                              {annualPrice}
                            </span>
                          )}

                          {trialText && (
                            <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                              {trialText}
                            </span>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </section>
            )}

            {/* ========================================================
                PLAN LOADING ERROR
            ======================================================== */}

            {plansError && (
              <div className="mb-6 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-900/50 dark:bg-amber-950/20">
                <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />

                <div>
                  <p className="text-sm font-medium text-amber-900 dark:text-amber-300">
                    Plan information is temporarily unavailable.
                  </p>

                  <p className="mt-1 text-sm text-amber-800 dark:text-amber-400">
                    You can still use the billing options below.
                  </p>
                </div>
              </div>
            )}

            {/* ========================================================
                CLERK PRICING TABLE
            ======================================================== */}

            <section className="mb-8 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
              <div className="border-b border-gray-200 p-5 dark:border-gray-700 sm:p-6">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  Subscribe Now
                </h2>

                <p className="mt-1 text-sm leading-6 text-gray-600 dark:text-gray-400">
                  Choose your billing option and continue through
                  Clerk's secure checkout.
                </p>
              </div>

              <div
                className={`w-full p-3 sm:p-5 lg:p-6 ${
                  isDarkMode ? 'bg-gray-800' : 'bg-white'
                }`}
              >
                <PricingTable
                  for="user"
                  appearance={clerkAppearance}
                  checkoutProps={{
                    appearance: clerkAppearance,
                  }}
                  newSubscriptionRedirectUrl="/billing"
                />
              </div>
            </section>

            {/* ========================================================
                BILLING MANAGEMENT
            ======================================================== */}

            <section className="mb-8 grid gap-4 md:grid-cols-2">
              <button
                type="button"
                onClick={() => setActiveTab('account')}
                className="group flex items-start gap-4 rounded-xl border border-gray-200 bg-white p-5 text-left shadow-sm transition-all hover:border-blue-300 hover:shadow-md dark:border-gray-700 dark:bg-gray-800 dark:hover:border-blue-700"
              >
                <div className="shrink-0 rounded-lg bg-blue-50 p-3 dark:bg-blue-900/20">
                  <CreditCard className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                </div>

                <div className="min-w-0 flex-1">
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    Payment & Subscription
                  </h3>

                  <p className="mt-1 text-sm leading-5 text-gray-600 dark:text-gray-400">
                    Manage your payment method, subscription, and
                    billing settings.
                  </p>

                  <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-blue-600 dark:text-blue-400">
                    Manage billing
                    <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </span>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setActiveTab('account')}
                className="group flex items-start gap-4 rounded-xl border border-gray-200 bg-white p-5 text-left shadow-sm transition-all hover:border-purple-300 hover:shadow-md dark:border-gray-700 dark:bg-gray-800 dark:hover:border-purple-700"
              >
                <div className="shrink-0 rounded-lg bg-purple-50 p-3 dark:bg-purple-900/20">
                  <UserRound className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                </div>

                <div className="min-w-0 flex-1">
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    Account Settings
                  </h3>

                  <p className="mt-1 text-sm leading-5 text-gray-600 dark:text-gray-400">
                    Manage your profile, security, and account
                    preferences.
                  </p>

                  <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-purple-600 dark:text-purple-400">
                    Open account settings
                    <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </span>
                </div>
              </button>
            </section>

            {/* ========================================================
                TRUST / BILLING OWNERSHIP
            ======================================================== */}

            <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
              <div className="flex items-start gap-3">
                <div className="shrink-0 rounded-lg bg-emerald-50 p-2 dark:bg-emerald-900/20">
                  <Shield className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                    Secure billing through Clerk
                  </h3>

                  <p className="mt-1 text-sm leading-6 text-gray-600 dark:text-gray-400">
                    Your subscription and payment information are
                    managed securely through Clerk. You can update
                    your billing details or manage your subscription
                    at any time from Account & Billing.
                  </p>
                </div>
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  )
}

export default Billing
